import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Input,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import {
  CheckIcon,
  InfoIcon,
  Loader2Icon,
  PencilIcon,
  PlusIcon,
  SparklesIcon,
  Trash2Icon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { notificationService } from '@/lib/services/notification.service';
import { useSetMediaTags } from '../api/mutations';
import { mediaFacetsQueryOptions } from '../api/facet-queries';
import { isIngestOwned, previewNormaliseTag, previewNormaliseTagKey } from '../lib/tag-selection';
import type { IngestionState } from '../model/media-tile';
import { IngestionNotice } from './IngestionNotice';
import { TagInput } from '@/shared/ui/TagInput';

/**
 * Normalises only what a person just typed, leaving an already-stored value exactly as it was.
 *
 * The server already normalised every value on the way in, so running the preview rule over the
 * whole array again would be a no-op for most of it -- except a value whose normalised form the
 * client and server compute slightly differently (there is no shared implementation, only two
 * kept in sync by hand), where re-normalising an untouched value could silently rewrite it.
 */
const normaliseNewValues = (existing: readonly string[], next: readonly string[]): string[] => {
  const known = new Set(existing);
  return next
    .map((value) => (known.has(value) ? value : previewNormaliseTag(value)))
    .filter((value) => value !== '');
};

/** Every value on the asset as its own chip, model-written ones first so the sparks read as a set. */
const flatten = (entries: [string, string[]][]): { key: string; value: string }[] =>
  entries.flatMap(([key, values]) => values.map((value) => ({ key, value })));

/**
 * An asset's tags: a list to read, and a mode to change them in.
 *
 * Reading is the common case by a wide margin -- a panel opens on every selection, and most of the
 * time somebody is checking what an asset carries, not editing it. So at rest this is one flow of
 * chips and nothing else. A combobox per key standing open behind them turned the section into a
 * form that was always mid-edit, and at the width of a detail panel each field was too narrow to
 * type into anyway.
 *
 * One list rather than a column each. Where a value came from is a property of the value, and a
 * spark on the chip says it better than a heading over half the section does: the two kinds sort
 * and wrap together, and an asset with nothing automatic on it no longer shows an empty column.
 *
 * Editing keeps the grouped shape, because that is the shape of the data -- a key holds several
 * values, and the combobox that adds one has to belong to a key. What ingest owns stays read-only
 * there and says why.
 *
 * One request per key change, matching the endpoint's own merge contract: `PATCH .../tags` only
 * touches the keys present in its body, so there is no whole-map diff to buffer behind a Save
 * button. Every add/remove fires immediately, the same immediate model `TagRail` already uses one
 * column over.
 */
export const MediaTagsEditor = ({
  title,
  mediaId,
  tags,
  ingestion,
  onRetryTagging,
  isRetrying = false,
}: {
  /**
   * The section's heading, supplied by the panel that places it. Rendered here rather than around
   * this component because the controls that share its line belong to a mode only this holds.
   */
  title: string;
  mediaId: string;
  tags: Record<string, string[]>;
  /**
   * Where ingestion has got to with this row, when the caller knows. Absent means catalogued, or a
   * surface that does not track it -- either way the editor says nothing about ingestion.
   */
  ingestion?: IngestionState;
  /**
   * Raised rather than handled, like the delete in the panel around this: the endpoint is
   * workspace-wide, so what a retry means (and what it invalidates) belongs to the surface that
   * owns the library rather than to one asset's tag list.
   */
  onRetryTagging?: () => void;
  isRetrying?: boolean;
}) => {
  const { t } = useTranslation('tools');
  const setTags = useSetMediaTags();
  const [editing, setEditing] = useState(false);
  const [addingKey, setAddingKey] = useState(false);
  const [newKeyText, setNewKeyText] = useState('');
  const [newKeyError, setNewKeyError] = useState<string | null>(null);
  const [draftKey, setDraftKey] = useState<string | null>(null);
  /**
   * Model-written keys a person has cleared but not yet refilled.
   *
   * Every other change here goes straight to the server, which is what makes the section feel
   * like a list rather than a form. These keys cannot be stored empty -- ingestion refills any it
   * finds that way -- but clearing the chip IS the first half of replacing it, so the emptiness
   * has to be allowed to exist somewhere while the new value is typed. It exists here: on screen
   * and nowhere else, until a value arrives and the whole replacement goes as one write.
   *
   * Nothing is lost by abandoning one. The server still holds the old value, so walking away
   * leaves the asset exactly as it was found.
   */
  const [cleared, setCleared] = useState<ReadonlySet<string>>(new Set());

  // Unscoped on purpose: the broadest available vocabulary for autocomplete/dedupe, independent of
  // whatever the grid this panel was opened from happens to be filtered to right now.
  const { data: facets } = useQuery(mediaFacetsQueryOptions({}));
  const suggestionsFor = (key: string): string[] =>
    facets?.find((facet) => facet.key === key)?.values.map((entry) => entry.value) ?? [];

  const entries = Object.entries(tags);
  // Split for reading order alone, so the model-written ones stay together and their sparks read
  // as a set. Both halves are editable: ingestion only revisits rows that are missing something,
  // so a correction to what the model said is not overwritten on a later run.
  const autoEntries = entries.filter(([key]) => isIngestOwned(key));
  const ownEntries = entries.filter(([key]) => !isIngestOwned(key));
  const existingKeys = new Set(entries.map(([key]) => key));

  const commitValues = (key: string, nextValues: string[]) => {
    setTags.mutate(
      { mediaId, tags: { [key]: nextValues } },
      {
        onSuccess: () => notificationService.showSuccess(t('brandMedia.tagsUpdated')),
        onError: (error) =>
          notificationService.showError(error.message || t('brandMedia.tagsUpdateFailed')),
      },
    );
  };

  /** What a group shows: what the server holds, unless it has been cleared on screen. */
  const shownValues = (key: string, values: string[]): string[] => (cleared.has(key) ? [] : values);

  /**
   * A change to one group, held back only while a model-written key sits empty.
   *
   * Clearing such a key is allowed to happen on screen and refused only when something tries to
   * store it, which is what makes replacing a value one gesture instead of two: the chip goes,
   * the field is empty, the new word is typed, and a single write carries the result. Everything
   * else, including clearing a key nobody's model writes, goes as it always did.
   */
  const changeValues = (key: string, values: string[], next: string[]) => {
    const nextValues = normaliseNewValues(shownValues(key, values), next);

    if (nextValues.length === 0 && isIngestOwned(key)) {
      setCleared((held) => new Set(held).add(key));
      return;
    }

    setCleared((held) => {
      const next = new Set(held);
      next.delete(key);
      return next;
    });
    commitValues(key, nextValues);
  };

  /** The model-written keys sitting empty on screen, which is what Done has to refuse. */
  const clearedKeys = [...cleared];

  const commitNewKey = () => {
    const normalised = previewNormaliseTagKey(newKeyText);
    if (normalised === '') {
      setNewKeyText('');
      return;
    }
    if (existingKeys.has(normalised)) {
      setNewKeyError(t('brandMedia.duplicateTagKey', { key: normalised }));
      return;
    }
    // No request yet: an empty group has nothing meaningful to persist. The first value typed into
    // the draft's own TagInput is what actually commits it, via the normal per-key path above.
    setDraftKey(normalised);
    setAddingKey(false);
    setNewKeyText('');
    setNewKeyError(null);
  };

  const startAdding = () => {
    setEditing(true);
    setAddingKey(true);
  };

  const addTag = (
    <AddTagControl
      t={t}
      addingKey={addingKey}
      setAddingKey={setAddingKey}
      newKeyText={newKeyText}
      setNewKeyText={setNewKeyText}
      newKeyError={newKeyError}
      setNewKeyError={setNewKeyError}
      onCommit={commitNewKey}
    />
  );

  if (!editing) {
    const chips = [...flatten(autoEntries), ...flatten(ownEntries)];

    /**
     * Nothing on the asset at all: no Edit, because there is nothing to edit yet and a mode toggle
     * over an empty list is a door onto an empty room. Straight to adding one instead.
     */
    const readAction =
      chips.length === 0 ? (
        <Button variant="ghost" size="mini" onClick={startAdding}>
          <PlusIcon /> {t('brandMedia.addTag')}
        </Button>
      ) : (
        <Button variant="ghost" size="mini" onClick={() => setEditing(true)}>
          <PencilIcon /> {t('brandMedia.editTags')}
        </Button>
      );

    return (
      <SectionFrame
        title={title}
        actions={
          <>
            {ingestion === 'unfinished' && onRetryTagging !== undefined && (
              <Button
                variant="outline"
                size="mini"
                disabled={isRetrying}
                title={t('brandMedia.retryTaggingHint')}
                onClick={onRetryTagging}
              >
                {/* The spark, not a generic redo: what this asks for is a model pass, and the
                    values it refills are the ones a person is told not to edit. Swapped for the
                    spinner while it runs rather than sitting beside it, so the button keeps its
                    width. */}
                {isRetrying ? (
                  <Loader2Icon aria-hidden="true" className="animate-spin" />
                ) : (
                  <SparklesIcon aria-hidden="true" />
                )}
                {t('brandMedia.retryTagging')}
              </Button>
            )}
            {readAction}
          </>
        }
      >
        {chips.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {chips.map(({ key, value }) => (
              <ReadTagChip key={`${key}:${value}`} t={t} tagKey={key} value={value} />
            ))}
          </div>
        )}
        {ingestion === undefined ? (
          chips.length === 0 && (
            <p className="text-sm text-muted-foreground">{t('brandMedia.noTagsYet')}</p>
          )
        ) : (
          <IngestionNotice
            state={ingestion}
            testId="tags-ingestion"
            pendingMessage={t('brandMedia.tagging')}
            // Only where the list is empty. Tags a person typed are still tags, so on an asset
            // showing chips the sentence would be contradicted by the row above it; what is missing
            // there is the automatic pass, which the retry in the heading already offers. The
            // pending wording stays either way -- a run still going may yet add to what is there.
            unfinishedMessage={chips.length === 0 ? t('brandMedia.taggingUnfinished') : undefined}
          />
        )}
      </SectionFrame>
    );
  }

  return (
    // Its own provider rather than the app's: this is shared code, mounted by more than one
    // host, and a tooltip whose provider is missing throws rather than degrading.
    <TooltipProvider>
      <SectionFrame
        title={title}
        // Almost nothing to save: every change went to the server as it was made, so this mostly
        // puts the section back to being read. It sits where Edit was, because it is the same
        // control. The one thing it has to check is a model-written key left cleared on screen,
        // which is the only state the section can hold that the server has not been told about.
        actions={
          <Button
            variant="secondary"
            size="mini"
            onClick={() => {
              if (clearedKeys.length > 0) {
                notificationService.showError(
                  t('brandMedia.modelTagNeedsValue', {
                    key: clearedKeys[0],
                    count: clearedKeys.length,
                  }),
                );
                return;
              }
              setEditing(false);
              setAddingKey(false);
              setDraftKey(null);
              setNewKeyText('');
              setNewKeyError(null);
            }}
          >
            <CheckIcon /> {t('brandMedia.doneEditingTags')}
          </Button>
        }
      >
        <div className="flex flex-col gap-4">
          {[...autoEntries, ...ownEntries].map(([key, values]) => {
            const auto = isIngestOwned(key);

            return (
              <div
                key={key}
                role="group"
                aria-label={key}
                data-owned={auto ? 'true' : undefined}
                className="flex min-w-0 flex-col gap-1"
              >
                <div className="flex items-center justify-between gap-1">
                  {/* The spark is what says a model wrote this, which stays worth knowing once the
                      field is editable: it is the difference between correcting a reading and
                      stating something of your own. */}
                  <span className="flex min-w-0 items-center gap-1.5 text-xs font-medium text-muted-foreground">
                    {auto && <SparklesIcon aria-hidden="true" className="size-3.5 shrink-0" />}
                    <span className="truncate">{key}</span>
                  </span>
                  {/* A model-written key may change value but not be emptied: the next sweep of
                      this row refills any such key it finds gone, so the bin would promise
                      something that quietly undoes itself. */}
                  {auto ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          aria-label={t('brandMedia.modelTagKeeps', { key })}
                          className="shrink-0"
                        >
                          <InfoIcon aria-hidden="true" className="size-3.5" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>{t('brandMedia.modelTagKeeps', { key })}</TooltipContent>
                    </Tooltip>
                  ) : (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          className="shrink-0"
                          aria-label={t('brandMedia.removeTagGroup', { key })}
                          onClick={() => commitValues(key, [])}
                        >
                          <Trash2Icon />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>{t('brandMedia.removeTagGroup', { key })}</TooltipContent>
                    </Tooltip>
                  )}
                </div>
                <TagInput
                  value={shownValues(key, values)}
                  onChange={(next) => changeValues(key, values, next)}
                  options={suggestionsFor(key)}
                  freeSolo
                  disabled={setTags.isPending}
                >
                  <TagInput.Chips
                    placeholder={
                      cleared.has(key)
                        ? t('brandMedia.modelTagAwaitingValue')
                        : t('brandMedia.addValuePlaceholder')
                    }
                    ariaLabel={key}
                  />
                  <TagInput.Content />
                </TagInput>
                {/* The one state in this section that is not already saved, so it has to say so
                    where it is rather than only when Done is pressed: leaving any other way keeps
                    the old value, and a field that looked empty would otherwise read as a removal
                    that worked. */}
                {/* The same destructive token the add-key error uses, a few lines down: both say
                    the section is holding something it cannot keep, and two colours for one kind
                    of problem would make neither read as the signal. */}
                {cleared.has(key) && (
                  <p className="text-xs text-destructive">{t('brandMedia.modelTagPendingValue')}</p>
                )}
              </div>
            );
          })}

          {draftKey !== null && (
            <div
              key={draftKey}
              role="group"
              aria-label={draftKey}
              className="flex min-w-0 flex-col gap-1"
            >
              <span className="truncate text-xs font-medium text-muted-foreground">{draftKey}</span>
              <TagInput
                value={[]}
                onChange={(next) => {
                  const values = normaliseNewValues([], next);
                  if (values.length > 0) {
                    commitValues(draftKey, values);
                    setDraftKey(null);
                  }
                }}
                options={suggestionsFor(draftKey)}
                freeSolo
                disabled={setTags.isPending}
              >
                <TagInput.Chips
                  placeholder={t('brandMedia.addValuePlaceholder')}
                  ariaLabel={draftKey}
                />
                <TagInput.Content />
              </TagInput>
            </div>
          )}

          {addTag}
        </div>
      </SectionFrame>
    </TooltipProvider>
  );
};

/**
 * The section's heading with its controls on the same line, right-aligned.
 *
 * Owned here rather than left to the panel's own `Section`, which takes a title and nothing else:
 * every control on that row changes what the section is showing, so the heading and the mode it
 * announces have to be rendered together. A row of buttons under the chips instead read as another
 * tag, and moved down the panel as the list grew.
 */
const SectionFrame = ({
  title,
  actions,
  children,
}: {
  title: string;
  actions: ReactNode;
  children: ReactNode;
}) => (
  <section className="flex flex-col gap-3">
    <div className="flex min-h-6 items-center justify-between gap-2">
      <h3 className="truncate text-sm font-medium text-foreground">{title}</h3>
      <div data-testid="tags-actions" className="flex shrink-0 items-center gap-1">
        {actions}
      </div>
    </div>
    {children}
  </section>
);

/**
 * One value, with the key it sits under and, when a model wrote it, the spark.
 *
 * Key and value in one chip because the list is no longer split by key: "christmas" on its own says
 * nothing about which field it answers. The key is set in the muted colour so the value still reads
 * first.
 *
 * The spark is decorative and an automatic chip names itself instead, so a screen reader hears where
 * the value came from rather than an unnamed icon before every automatic value. A manual chip needs
 * no such label: its two words are already the whole of what it says.
 */
const ReadTagChip = ({
  t,
  tagKey,
  value,
}: {
  t: TFunction<'tools'>;
  tagKey: string;
  value: string;
}) => {
  const auto = isIngestOwned(tagKey);

  return (
    <Badge
      variant="secondary"
      size="sm"
      roundness="full"
      aria-label={auto ? t('brandMedia.autoTagLabel', { key: tagKey, value }) : undefined}
    >
      {auto && <SparklesIcon aria-hidden="true" className="size-3 shrink-0" />}
      <span className="text-muted-foreground">{tagKey}</span>
      <span>{value}</span>
    </Badge>
  );
};

const AddTagControl = ({
  t,
  addingKey,
  setAddingKey,
  newKeyText,
  setNewKeyText,
  newKeyError,
  setNewKeyError,
  onCommit,
}: {
  t: TFunction<'tools'>;
  addingKey: boolean;
  setAddingKey: (value: boolean) => void;
  newKeyText: string;
  setNewKeyText: (value: string) => void;
  newKeyError: string | null;
  setNewKeyError: (value: string | null) => void;
  onCommit: () => void;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (addingKey) inputRef.current?.focus();
  }, [addingKey]);

  if (!addingKey) {
    return (
      <Button variant="ghost" size="mini" className="w-fit" onClick={() => setAddingKey(true)}>
        <PlusIcon /> {t('brandMedia.addTag')}
      </Button>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <Input
        ref={inputRef}
        value={newKeyText}
        onChange={(event) => {
          setNewKeyText(event.target.value);
          setNewKeyError(null);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            onCommit();
          }
          if (event.key === 'Escape') {
            setAddingKey(false);
            setNewKeyText('');
            setNewKeyError(null);
          }
        }}
        onBlur={() => {
          if (newKeyText.trim() === '') setAddingKey(false);
        }}
        placeholder={t('brandMedia.addTagKeyPlaceholder')}
        className="h-8 text-xs"
      />
      {newKeyError !== null && <p className="text-xs text-destructive">{newKeyError}</p>}
    </div>
  );
};
